/**
 * A single vault's overview (route `/vaults/:vaultId`). Shows the vault switcher,
 * an add-item card (kind picker → `ItemForm`), a client-side search filter over
 * decrypted fields (zero-knowledge / INV-2), and the vault's items as compact
 * rows linking to their detail route. Full reveal/edit/delete live on the item
 * detail page (`ItemDetail`).
 */
import { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ApiError } from '@heivol-locker/vault-client';
import type { ItemKind, ItemPayload } from '@heivol-locker/shared-types';
import { Button, Card, ErrorNote, Field, InfoNote, Input } from '../components/ui';
import {
  ITEM_KINDS,
  ItemForm,
  ItemRow,
  KIND_LABELS,
  itemMatches,
  useClipboardCopy,
} from '../components/items';
import { VaultSwitcher } from '../components/VaultSwitcher';
import { useSession } from '../store/session';
import { useVaultData, useVaultItems } from '../store/vault-data';

export function VaultOverview() {
  const { vaultId } = useParams();
  const { vaults, loading: vaultsLoading } = useVaultData();
  const { items, loading, error, notice, addItem } = useVaultItems(vaultId);
  const { copiedId, copy } = useClipboardCopy();

  const [addKind, setAddKind] = useState<ItemKind>('login');
  const [addKey, setAddKey] = useState(0);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const wipeCancel = useSession((s) => s.wipeCancel);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelResult, setCancelResult] = useState<'ok' | 'none' | 'error' | null>(null);

  const vault = vaults.find((v) => v.id === vaultId);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) {
      return items;
    }
    return items.filter((it) => itemMatches(it.payload, q));
  }, [items, query]);

  // Stale/invalid vault id in the URL → bounce to the vault index once vaults load.
  if (!vaultsLoading && vaultId !== undefined && vault === undefined && vaults.length > 0) {
    return <Navigate to="/vaults" replace />;
  }

  const cancelWipe = (): void => {
    setCancelBusy(true);
    setCancelResult(null);
    void wipeCancel()
      .then(() => setCancelResult('ok'))
      .catch((err: unknown) =>
        setCancelResult(err instanceof ApiError && err.status === 404 ? 'none' : 'error'),
      )
      .finally(() => setCancelBusy(false));
  };

  const submitAdd = (payload: ItemPayload): void => {
    setAdding(true);
    void addItem(payload)
      .then(() => setAddKey((k) => k + 1)) // remount ItemForm → reset fields
      .catch(() => undefined)
      .finally(() => setAdding(false));
  };

  return (
    <div className="space-y-6">
      <VaultSwitcher activeVaultId={vaultId ?? ''} />

      <div>
        <h1 className="text-xl font-semibold text-text">{vault?.name ?? 'Vault'}</h1>
        <p className="text-sm text-text-secondary">
          {loading ? 'Loading…' : `${items.length} item${items.length === 1 ? '' : 's'}`}
        </p>
      </div>

      {error !== null && <ErrorNote>{error}</ErrorNote>}
      {notice !== null && (
        <p
          role="status"
          className="rounded-md border border-border bg-surface-alt px-3 py-2 text-sm text-text-secondary"
        >
          {notice}
        </p>
      )}

      <Card className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="mr-2 text-sm font-semibold text-text">Add</h2>
          {ITEM_KINDS.map((k) => (
            <Button
              key={k}
              type="button"
              variant={addKind === k ? 'primary' : 'ghost'}
              onClick={() => {
                setAddKind(k);
                setAddKey((n) => n + 1);
              }}
            >
              {KIND_LABELS[k]}
            </Button>
          ))}
        </div>
        <ItemForm
          key={`${addKind}-${addKey}`}
          kind={addKind}
          submitLabel={`Add ${KIND_LABELS[addKind].toLowerCase()}`}
          busyLabel="Encrypting…"
          busy={adding}
          onSubmit={submitAdd}
        />
      </Card>

      {items.length > 0 && (
        <Field label="Search">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by title, username, website, cardholder…"
            type="search"
            aria-label="Search items"
          />
        </Field>
      )}

      <div className="space-y-2">
        {items.length === 0 && !loading && (
          <p className="text-sm text-text-secondary">No items yet — add your first above.</p>
        )}
        {items.length > 0 && filtered.length === 0 && (
          <p className="text-sm text-text-secondary">No items match “{query.trim()}”.</p>
        )}
        {filtered.map((it) => (
          <ItemRow
            key={it.id}
            item={it}
            to={`/vaults/${vaultId}/items/${it.id}`}
            onCopy={copy}
            copied={copiedId === it.id}
          />
        ))}
      </div>

      <Card className="space-y-3">
        <h2 className="text-sm font-semibold text-text">Didn&rsquo;t ask to wipe your vault?</h2>
        <p className="text-sm text-text-secondary">
          If a vault wipe was requested for this account, you can cancel it here. It works from any
          device, and also restores a vault that was wiped in the last 72 hours.
        </p>
        <Button type="button" variant="ghost" onClick={cancelWipe} disabled={cancelBusy}>
          {cancelBusy ? 'Cancelling…' : 'A wipe was requested — cancel it'}
        </Button>
        {cancelResult === 'ok' && <InfoNote>Any pending wipe has been cancelled.</InfoNote>}
        {cancelResult === 'none' && <InfoNote>No wipe is pending on this account.</InfoNote>}
        {cancelResult === 'error' && <ErrorNote>Could not cancel the wipe. Try again.</ErrorNote>}
      </Card>
    </div>
  );
}
